/**
 * Kingdom Factor - Reusable Header Navigation
 * Used across all public-facing pages
 */

import { useState } from "react"
import { Link } from "@inertiajs/react"
import { Menu, X, ChevronDown } from "lucide-react"
import logoDark from "@/assets/logos/logo-dark.svg"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer"
import { GetStartedDialog } from "@/components/GetStartedDialog"

// The desktop bar has three widths (the logo is the way home, so no "Home"):
//   wide   (WIDE and up):    For Members · For Coaches · For Emerging Leaders · KF Ambassador · …
//   medium (MEDIUM to WIDE): the same without "For"
//   narrow (lg to MEDIUM):   Members · Coaches · Get Involved ▾ (the `involved` links) · …
// Members and Coaches always show: they are the two main audiences.
const navLinks = [
  { href: "/member", label: "Members", forPrefix: true, id: "member" },
  { href: "/coach", label: "Coaches", forPrefix: true, id: "coach" },
  // Ascend Leadership stays on its own site for now — link out rather than route in
  { href: "https://ascend.kingdomfactor.us", label: "Emerging Leaders", forPrefix: true, id: "ascend", isExternal: true, involved: true },
  { href: "/ambassador", label: "KF Ambassador", id: "ambassador", involved: true },
  { href: "/collective", label: "Leader Stories", id: "collective" },
]
const involvedLinks = navLinks.filter((link) => link.involved)
const fullLabel = (link) => (link.forPrefix ? `For ${link.label}` : link.label)

const aboutLinks = [
  { href: "/our-calling", label: "Our Calling", id: "our-calling" },
  { href: "/statement-of-faith", label: "Statement of Practice", id: "statement-of-faith" },
]

// All links for mobile menu (flat list)
const allMobileLinks = [
  { href: "/", label: "Home", id: "home" },
  ...navLinks.map((link) => ({ ...link, label: fullLabel(link) })),
  ...aboutLinks,
]

export function Header({ activePage = "home", scheduleCallUrl = "#" }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [involvedOpen, setInvolvedOpen] = useState(false)
  const [getStartedOpen, setGetStartedOpen] = useState(false)

  // Check if current page is in the About dropdown
  const isAboutActive = aboutLinks.some((link) => link.id === activePage)
  const isInvolvedActive = involvedLinks.some((link) => link.id === activePage)

  const renderNavLink = (link) => {
    const Tag = link.isExternal ? "a" : Link
    return (
      <Tag
        key={link.id}
        href={link.href}
        className={`text-sm uppercase tracking-wider transition-colors whitespace-nowrap ${
          link.involved ? "hidden min-[1180px]:inline" : ""
        } ${
          activePage === link.id
            ? "text-white"
            : "text-white/70 hover:text-white"
        }`}
      >
        {link.forPrefix && <span className="hidden min-[1360px]:inline">For </span>}
        {link.label}
      </Tag>
    )
  }

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-kf-gradient backdrop-blur-md border-b border-white/10 print:hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center gap-4">
            <Link href="/">
              <img src={logoDark} alt="Kingdom Factor" className="h-10" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          {/* Three widths; see navLinks. Every item stays on one line. */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.slice(0, navLinks.indexOf(involvedLinks.at(-1)) + 1).map(renderNavLink)}
            {/* Narrow: Emerging Leaders and KF Ambassador fold into one menu */}
            <DropdownMenu open={involvedOpen} onOpenChange={setInvolvedOpen}>
              <DropdownMenuTrigger
                className={`min-[1180px]:hidden text-sm uppercase tracking-wider whitespace-nowrap transition-colors flex items-center gap-1 outline-none ${
                  isInvolvedActive ? "text-white" : "text-white/70 hover:text-white"
                }`}
              >
                Get Involved
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${involvedOpen ? "rotate-180" : ""}`} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="bg-kf-gray border-white/10 min-w-[200px]">
                {involvedLinks.map((link) => {
                  const Tag = link.isExternal ? "a" : Link
                  return (
                    <DropdownMenuItem key={link.id} asChild>
                      <Tag
                        href={link.href}
                        className={`text-sm uppercase tracking-wider cursor-pointer ${
                          activePage === link.id ? "text-white bg-white/10" : "text-white/70 hover:text-white hover:bg-white/10"
                        }`}
                      >
                        {fullLabel(link)}
                      </Tag>
                    </DropdownMenuItem>
                  )
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            {navLinks.slice(navLinks.indexOf(involvedLinks.at(-1)) + 1).map(renderNavLink)}

            {/* About Dropdown */}
            <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <DropdownMenuTrigger
                className={`text-sm uppercase tracking-wider whitespace-nowrap transition-colors flex items-center gap-1 outline-none ${
                  isAboutActive ? "text-white" : "text-white/70 hover:text-white"
                }`}
              >
                About
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="center"
                className="bg-kf-gray border-white/10 min-w-[180px]"
              >
                {aboutLinks.map((link) => (
                  <DropdownMenuItem key={link.id} asChild>
                    <Link
                      href={link.href}
                      className={`text-sm uppercase tracking-wider cursor-pointer ${
                        activePage === link.id
                          ? "text-white bg-white/10"
                          : "text-white/70 hover:text-white hover:bg-white/10"
                      }`}
                    >
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Desktop CTA + Mobile Menu Button */}
          <div className="flex items-center gap-4">
            {/* Desktop CTA */}
            <Button variant="kf-secondary" className="hidden lg:flex" onClick={() => setGetStartedOpen(true)}>
              Get Started
            </Button>

            {/* Mobile Menu Button */}
            <Drawer direction="right" open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <DrawerTrigger asChild>
                <button
                  className="lg:hidden text-white p-2 -mr-2"
                  aria-label="Open menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
              </DrawerTrigger>
              <DrawerContent className="bg-kf-gray border-l-white/10 h-full">
                <DrawerHeader className="border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <DrawerTitle className="text-white font-display">Menu</DrawerTitle>
                    <DrawerClose asChild>
                      <button className="text-white/70 hover:text-white p-1" aria-label="Close menu">
                        <X className="w-5 h-5" />
                      </button>
                    </DrawerClose>
                  </div>
                </DrawerHeader>

                {/* Mobile Nav Links */}
                <div className="flex flex-col p-4 gap-1">
                  {allMobileLinks.map((link) => {
                    const Tag = link.isExternal ? "a" : Link
                    return (
                      <Tag
                        key={link.id}
                        href={link.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`py-3 px-4 rounded-lg text-sm uppercase tracking-wider transition-colors ${
                          activePage === link.id
                            ? "text-white bg-white/10"
                            : "text-white/70 hover:text-white hover:bg-white/5"
                        }`}
                      >
                        {link.label}
                      </Tag>
                    )
                  })}
                </div>

                {/* Mobile CTA */}
                <div className="mt-auto p-4 border-t border-white/10 space-y-3">
                  <Button
                    variant="kf-secondary"
                    className="w-full"
                    onClick={() => {
                      setMobileMenuOpen(false)
                      setGetStartedOpen(true)
                    }}
                  >
                    Get Started
                  </Button>
                  <Button
                    variant="kf-outline"
                    className="w-full border-white/30 text-white hover:border-white hover:text-white"
                    asChild
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Link href="/login">Member Login</Link>
                  </Button>
                </div>
              </DrawerContent>
            </Drawer>
          </div>
        </div>
      </div>
      <GetStartedDialog open={getStartedOpen} onOpenChange={setGetStartedOpen} />
    </nav>
  )
}
